import React, { useState, useEffect, useCallback, useRef } from 'react';
import axiosInstance from '../utils/axiosConfig';
import AddCustomsTariffsModal from './AddCustomsTariffsModal';
import EditCustomsTariffsModal from './EditCustomsTariffsModal';
import XLSX from 'xlsx-js-style';
import '../styles/nassar-theme.css';
import './CustomsTariffs.css';

// Module-level cache: keeps search filters & current page alive across remounts (no localStorage)
const searchStateCache = {
    searchQuery: '',
    selectedSearchFields: [],
    fieldSearchValues: {},
    showSearchOptions: false,
    currentPage: 1,
};

const CustomsTariffs = () => {
    const [tariffs, setTariffs] = useState([]);
    const [viewMode, setViewMode] = useState('table'); // 'table' or 'card'
    const [isLoading, setIsLoading] = useState(true);
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [selectedTariffForEdit, setSelectedTariffForEdit] = useState(null);
    const [error, setError] = useState('');
    const [selectedTariffItems, setSelectedTariffItems] = useState({}); // Track selected tariff item per tariff row

    // Pagination state — currentPage initialized from cache
    const [currentPage, setCurrentPage] = useState(searchStateCache.currentPage);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);

    // Search state — initialized from module-level cache so values persist on navigation
    const [searchQuery, setSearchQuery] = useState(searchStateCache.searchQuery);
    const [showSearchOptions, setShowSearchOptions] = useState(searchStateCache.showSearchOptions);
    const [selectedSearchFields, setSelectedSearchFields] = useState(searchStateCache.selectedSearchFields);
    const [fieldSearchValues, setFieldSearchValues] = useState(searchStateCache.fieldSearchValues);

    // Debounce auto-search
    const debounceRef = useRef(null);
    const isFirstRender = useRef(true);

    // Focus restoration after auto-search
    const activeInputRef = useRef(null);
    const fieldInputRefs = useRef({});
    const generalSearchRef = useRef(null);
    const prevLoadingRef = useRef(false);

    // Available search fields - ordered same as table columns
    const searchFields = [
        { key: 'clearance_file_num', label: 'رقم ملف التخليص' },
        { key: 'custom_tariff', label: 'التعريفة الجمركية' },
        { key: 'desc_item_ar', label: 'وصف الأصناف (عربي)' },
        { key: 'desc_item_en', label: 'وصف الأصناف (إنجليزي)' },
        { key: 'origin_country', label: 'بلد المنشأ' },
    ];

    // Tariff-item-related search fields (like container fields in ClearanceFiles)
    const tariffItemRelatedFields = React.useMemo(() =>
        ['custom_tariff', 'desc_item_ar', 'desc_item_en'],
    []);

    const fetchTariffs = useCallback(async (page = 1, overrides = null) => {
        setIsLoading(true);
        setError('');
        try {
            const query = overrides !== null ? overrides.searchQuery : searchQuery;
            const fields = overrides !== null ? overrides.selectedSearchFields : selectedSearchFields;
            const fieldValues = overrides !== null ? overrides.fieldSearchValues : fieldSearchValues;

            let url = `/customs-tariffs/?page=${page}`;
            
            if (fields.length > 0) {
                fields.forEach(field => {
                    const val = (fieldValues[field] || '').trim();
                    if (val) {
                        url += `&filter_${field}=${encodeURIComponent(val)}`;
                    }
                });
            } else if (query) {
                url += `&search=${encodeURIComponent(query)}`;
                searchFields.forEach(f => {
                    url += `&search_fields=${f.key}`;
                });
            }

            const response = await axiosInstance.get(url);
            
            let tariffsData;
            if (response.data.results) {
                tariffsData = response.data.results;
                setTariffs(tariffsData);
                setTotalPages(response.data.total_pages);
                setTotalCount(response.data.count);
                setCurrentPage(response.data.current_page);
            } else {
                tariffsData = response.data;
                setTariffs(tariffsData);
                setTotalPages(1);
                setTotalCount(tariffsData.length);
            }

            const initialSelected = {};
            const isTariffItemSearch = fields.some(field =>
                tariffItemRelatedFields.includes(field)
            );
            const hasItemValues = tariffItemRelatedFields.some(f => (fieldValues[f] || '').trim());

            tariffsData.forEach(tariff => {
                if (tariff.tariffs_list && tariff.tariffs_list.length > 0) {
                    if (isTariffItemSearch && hasItemValues) {
                        const matchingItem = tariff.tariffs_list.find(item => {
                            const ctVal = (fieldValues['custom_tariff'] || '').trim().toLowerCase();
                            const arVal = (fieldValues['desc_item_ar'] || '').trim().toLowerCase();
                            const enVal = (fieldValues['desc_item_en'] || '').trim().toLowerCase();
                            if (ctVal && item.custom_tariff_name?.toLowerCase().includes(ctVal)) return true;
                            if (arVal && item.desc_item_ar?.toLowerCase().includes(arVal)) return true;
                            if (enVal && item.desc_item_en?.toLowerCase().includes(enVal)) return true;
                            return false;
                        });
                        initialSelected[tariff.id] = matchingItem ? matchingItem.id : tariff.tariffs_list[0].id;
                    } else if (query) {
                        const q = query.trim().toLowerCase();
                        const matchingItem = tariff.tariffs_list.find(item => {
                            if (item.custom_tariff_name?.toLowerCase().includes(q)) return true;
                            if (item.desc_item_ar?.toLowerCase().includes(q)) return true;
                            if (item.desc_item_en?.toLowerCase().includes(q)) return true;
                            return false;
                        });
                        initialSelected[tariff.id] = matchingItem ? matchingItem.id : tariff.tariffs_list[0].id;
                    } else {
                        initialSelected[tariff.id] = tariff.tariffs_list[0].id;
                    }
                }
            });
            setSelectedTariffItems(prev => ({...prev, ...initialSelected}));
        } catch (err) {
            setError('حدث خطأ أثناء جلب البيانات');
            console.error('Error fetching tariffs:', err);
        } finally {
            setIsLoading(false);
        }
    }, [searchQuery, selectedSearchFields, fieldSearchValues, tariffItemRelatedFields, searchFields]);

    useEffect(() => {
        fetchTariffs(searchStateCache.currentPage || 1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Persist current search & pagination state into the module-level cache on every change
    useEffect(() => {
        searchStateCache.searchQuery = searchQuery;
        searchStateCache.selectedSearchFields = selectedSearchFields;
        searchStateCache.fieldSearchValues = fieldSearchValues;
        searchStateCache.showSearchOptions = showSearchOptions;
        searchStateCache.currentPage = currentPage;
    }, [
        searchQuery,
        selectedSearchFields,
        fieldSearchValues,
        showSearchOptions,
        currentPage,
    ]);

    // Auto-search on typing (debounced)
    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            setCurrentPage(1);
            fetchTariffs(1, {
                searchQuery,
                selectedSearchFields,
                fieldSearchValues
            });
        }, 400);
        return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchQuery, fieldSearchValues]);

    useEffect(() => {
        if (prevLoadingRef.current && !isLoading && activeInputRef.current) {
            requestAnimationFrame(() => {
                if (activeInputRef.current === '__general__' && generalSearchRef.current) {
                    generalSearchRef.current.focus();
                } else if (fieldInputRefs.current[activeInputRef.current]) {
                    fieldInputRefs.current[activeInputRef.current].focus();
                }
            });
        }
        prevLoadingRef.current = isLoading;
    }, [isLoading]);

    const highlightText = (text, fieldKey) => {
        if (!text || text === '-') return text;
        const textStr = String(text);
        let searchTerm = '';
        if (selectedSearchFields.length > 0 && fieldKey) {
            searchTerm = (fieldSearchValues[fieldKey] || '').trim();
        } else if (selectedSearchFields.length === 0) {
            searchTerm = searchQuery.trim();
        }
        if (!searchTerm) return textStr;
        try {
            const escaped = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            const regex = new RegExp(`(${escaped})`, 'gi');
            const parts = textStr.split(regex);
            if (parts.length <= 1) return textStr;
            return parts.map((part, i) =>
                i % 2 === 1 ? <span key={i} style={{ backgroundColor: '#fff176', padding: '0 1px', borderRadius: '2px' }}>{part}</span> : part
            );
        } catch { return textStr; }
    };

    const handleSearch = (e) => {
        if (e) e.preventDefault();
        setCurrentPage(1);
        fetchTariffs(1, {
            searchQuery,
            selectedSearchFields,
            fieldSearchValues
        });
    };

    const handleClearSearch = () => {
        setSearchQuery('');
        setSelectedSearchFields([]);
        setFieldSearchValues({});
        setCurrentPage(1);
        fetchTariffs(1, {
            searchQuery: '',
            selectedSearchFields: [],
            fieldSearchValues: {}
        });
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages) {
            setCurrentPage(newPage);
            fetchTariffs(newPage);
        }
    };

    const toggleSearchField = (fieldKey) => {
        setSelectedSearchFields(prev => {
            if (prev.includes(fieldKey)) {
                setFieldSearchValues(fv => {
                    const updated = { ...fv };
                    delete updated[fieldKey];
                    return updated;
                });
                const newFields = prev.filter(f => f !== fieldKey);
                if (newFields.length === 0) {
                    setSearchQuery('');
                }
                return newFields;
            }
            setSearchQuery('');
            return [...prev, fieldKey];
        });
    };

    const handleTariffItemChange = (tariffId, itemId) => {
        setSelectedTariffItems(prev => ({
            ...prev,
            [tariffId]: parseInt(itemId)
        }));
    };

    const getSelectedTariffItem = (tariff) => {
        if (!tariff.tariffs_list || tariff.tariffs_list.length === 0) {
            return null;
        }
        const selectedId = selectedTariffItems[tariff.id];
        return tariff.tariffs_list.find(item => item.id === selectedId) || tariff.tariffs_list[0];
    };

    const handleAddSuccess = () => {
        fetchTariffs(currentPage);
    };

    const handleEditTariff = (tariff) => {
        setSelectedTariffForEdit(tariff);
        setIsEditModalOpen(true);
    };

    const handleEditSuccess = () => {
        fetchTariffs(currentPage);
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleString('en-GB', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
        });
    };

    // Pagination component
    const Pagination = () => (
        <div className="pagination-container" style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '0.5rem',
            marginTop: '1.5rem',
            padding: '1rem',
            flexWrap: 'wrap'
        }}>
            <button
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1}
                className="pagination-btn"
                style={{
                    padding: '0.5rem 1rem',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    backgroundColor: currentPage === 1 ? '#f5f5f5' : '#fff',
                    color: currentPage === 1 ? '#aaa' : '#03428d',
                    cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                    fontWeight: '600',
                    transition: 'all 0.2s ease'
                }}
            >
                الأولى
            </button>
            <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="pagination-btn"
                style={{
                    padding: '0.5rem 1rem',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    backgroundColor: currentPage === 1 ? '#f5f5f5' : '#fff',
                    color: currentPage === 1 ? '#aaa' : '#03428d',
                    cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                    fontWeight: '600',
                    transition: 'all 0.2s ease'
                }}
            >
                السابق
            </button>
            
            <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.5rem 1rem',
                backgroundColor: '#03428d',
                color: '#fff',
                borderRadius: '8px',
                fontWeight: '600'
            }}>
                <span>صفحة {currentPage} من {totalPages}</span>
                <span style={{ opacity: 0.8, fontSize: '0.85rem' }}>({totalCount} نتيجة)</span>
            </div>
            
            <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="pagination-btn"
                style={{
                    padding: '0.5rem 1rem',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    backgroundColor: currentPage === totalPages ? '#f5f5f5' : '#fff',
                    color: currentPage === totalPages ? '#aaa' : '#03428d',
                    cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                    fontWeight: '600',
                    transition: 'all 0.2s ease'
                }}
            >
                التالي
            </button>
            <button
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage === totalPages}
                className="pagination-btn"
                style={{
                    padding: '0.5rem 1rem',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    backgroundColor: currentPage === totalPages ? '#f5f5f5' : '#fff',
                    color: currentPage === totalPages ? '#aaa' : '#03428d',
                    cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                    fontWeight: '600',
                    transition: 'all 0.2s ease'
                }}
            >
                الأخيرة
            </button>
        </div>
    );

    // Export to Excel
    const [isExporting, setIsExporting] = useState(false);

    // Export choice & selection modal state
    const [exportChoiceModalOpen, setExportChoiceModalOpen] = useState(false);
    const [exportSelectModalOpen, setExportSelectModalOpen] = useState(false);
    const [selectExportFiles, setSelectExportFiles] = useState([]);
    const [selectExportPage, setSelectExportPage] = useState(1);
    const [selectExportTotalPages, setSelectExportTotalPages] = useState(1);
    const [selectExportTotalCount, setSelectExportTotalCount] = useState(0);
    const [selectedExportIds, setSelectedExportIds] = useState(() => new Set());
    const [isLoadingSelectExport, setIsLoadingSelectExport] = useState(false);
    const selectExportDebounceRef = useRef(null);

    // Modal-specific search/filter state
    const [mSearchQuery, setMSearchQuery] = useState('');
    const [mShowSearchOptions, setMShowSearchOptions] = useState(false);
    const [mSelectedSearchFields, setMSelectedSearchFields] = useState([]);
    const [mFieldSearchValues, setMFieldSearchValues] = useState({});
    const mGeneralSearchRef = useRef(null);
    const mFieldInputRefs = useRef({});
    const mActiveInputRef = useRef(null);
    const mPrevLoadingRef = useRef(false);

    // Build and download Excel from data
    const buildAndDownloadExcel = (allTariffs) => {
        const rows = [];
        allTariffs.forEach(tariff => {
            const items = tariff.tariffs_list && tariff.tariffs_list.length > 0 ? tariff.tariffs_list : [null];
            items.forEach(item => {
                rows.push({
                    'رقم ملف التخليص': tariff.clearance_file_num || '',
                    'التعريفة الجمركية': item ? item.custom_tariff_name || '' : '',
                    'بلد المنشأ': tariff.origin_country_name || '',
                    'وصف الأصناف (عربي)': item ? item.desc_item_ar || '' : '',
                    'وصف الأصناف (إنجليزي)': item ? item.desc_item_en || '' : '',
                    'ملاحظات': item ? item.note || '' : '',
                    'تاريخ الإنشاء': tariff.created_at ? new Date(tariff.created_at).toLocaleString('en-GB', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false }) : ''
                });
            });
        });

        if (rows.length === 0) {
            setError('لا توجد بيانات للتصدير');
            return;
        }

        const worksheet = XLSX.utils.json_to_sheet(rows);
        worksheet['!rtl'] = true;

        const headerKeys = Object.keys(rows[0] || {});
        const headerStyle = {
            fill: { fgColor: { rgb: '217346' } },
            font: { bold: true, color: { rgb: 'FFFFFF' }, sz: 12 },
            alignment: { horizontal: 'center', vertical: 'center', readingOrder: 2 }
        };
        const cellStyle = {
            alignment: { horizontal: 'center', vertical: 'center', readingOrder: 2 }
        };
        headerKeys.forEach((key, colIdx) => {
            const cellRef = XLSX.utils.encode_cell({ r: 0, c: colIdx });
            if (worksheet[cellRef]) worksheet[cellRef].s = headerStyle;
        });

        const range = XLSX.utils.decode_range(worksheet['!ref']);
        for (let r = 1; r <= range.e.r; r++) {
            for (let c = 0; c <= range.e.c; c++) {
                const cellRef = XLSX.utils.encode_cell({ r, c });
                if (worksheet[cellRef]) worksheet[cellRef].s = cellStyle;
            }
        }

        const colWidths = headerKeys.map(key => ({
            wch: Math.max(key.length * 2, ...rows.map(r => String(r[key] || '').length)) + 2
        }));
        worksheet['!cols'] = colWidths;

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'التعريفات الجمركية');
        workbook.Workbook = workbook.Workbook || {};
        workbook.Workbook.Views = [{ RTL: true }];

        const now = new Date();
        const dateStr = now.toLocaleDateString('en-GB').replace(/\//g, '-');
        const timeStr = now.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false }).replace(/:/g, '-');
        const fileName = `التعريفات_الجمركية_${dateStr}_${timeStr}.xlsx`;

        XLSX.writeFile(workbook, fileName);
    };

    const exportToExcel = async () => {
        setIsExporting(true);
        try {
            let url = '/customs-tariffs/';
            const params = [];
            if (selectedSearchFields.length > 0) {
                selectedSearchFields.forEach(field => {
                    const val = (fieldSearchValues[field] || '').trim();
                    if (val) params.push(`filter_${field}=${encodeURIComponent(val)}`);
                });
            } else if (searchQuery) {
                params.push(`search=${encodeURIComponent(searchQuery)}`);
                searchFields.forEach(f => params.push(`search_fields=${f.key}`));
            }
            if (params.length > 0) url += '?' + params.join('&');

            const response = await axiosInstance.get(url);
            const allTariffs = Array.isArray(response.data) ? response.data : (response.data.results || []);
            buildAndDownloadExcel(allTariffs);
        } catch (err) {
            console.error('Error exporting to Excel:', err);
            setError('حدث خطأ أثناء التصدير إلى Excel');
        } finally {
            setIsExporting(false);
        }
    };

    // Fetch files for selection modal
    const fetchModalFiles = useCallback(async (page = 1, overrides = null) => {
        setIsLoadingSelectExport(true);
        try {
            const query = overrides !== null ? overrides.searchQuery : mSearchQuery;
            const fields = overrides !== null ? overrides.selectedSearchFields : mSelectedSearchFields;
            const fieldValues = overrides !== null ? overrides.fieldSearchValues : mFieldSearchValues;

            let url = `/customs-tariffs/?page=${page}`;

            if (fields.length > 0) {
                fields.forEach(field => {
                    const val = (fieldValues[field] || '').trim();
                    if (val) url += `&filter_${field}=${encodeURIComponent(val)}`;
                });
            } else if (query) {
                url += `&search=${encodeURIComponent(query)}`;
                searchFields.forEach(f => { url += `&search_fields=${f.key}`; });
            }

            const response = await axiosInstance.get(url);
            let tariffsData;
            if (response.data.results) {
                tariffsData = response.data.results;
                setSelectExportFiles(tariffsData);
                setSelectExportTotalPages(response.data.total_pages);
                setSelectExportTotalCount(response.data.count);
                setSelectExportPage(response.data.current_page);
            } else {
                tariffsData = response.data;
                setSelectExportFiles(tariffsData);
                setSelectExportTotalPages(1);
                setSelectExportTotalCount(tariffsData.length);
                setSelectExportPage(1);
            }
        } catch (err) {
            console.error('Error fetching files for selection:', err);
        } finally {
            setIsLoadingSelectExport(false);
        }
    }, [mSearchQuery, mSelectedSearchFields, mFieldSearchValues, searchFields]);

    const openExportSelectModal = () => {
        setExportChoiceModalOpen(false);
        setMSearchQuery('');
        setMShowSearchOptions(false);
        setMSelectedSearchFields([]);
        setMFieldSearchValues({});
        setSelectedExportIds(new Set());
        setSelectExportPage(1);
        setExportSelectModalOpen(true);
        fetchModalFiles(1, { searchQuery: '', selectedSearchFields: [], fieldSearchValues: {} });
    };

    const handleModalSearch = (e) => {
        if (e) e.preventDefault();
        setSelectExportPage(1);
        fetchModalFiles(1, { searchQuery: mSearchQuery, selectedSearchFields: mSelectedSearchFields, fieldSearchValues: mFieldSearchValues });
    };

    const handleModalClearSearch = () => {
        setMSearchQuery('');
        setMSelectedSearchFields([]);
        setMFieldSearchValues({});
        setSelectExportPage(1);
        fetchModalFiles(1, { searchQuery: '', selectedSearchFields: [], fieldSearchValues: {} });
    };

    const handleModalPageChange = (newPage) => {
        if (newPage >= 1 && newPage <= selectExportTotalPages) {
            setSelectExportPage(newPage);
            fetchModalFiles(newPage);
        }
    };

    const toggleModalSearchField = (fieldKey) => {
        setMSelectedSearchFields(prev => {
            if (prev.includes(fieldKey)) {
                setMFieldSearchValues(fv => { const updated = { ...fv }; delete updated[fieldKey]; return updated; });
                const newFields = prev.filter(f => f !== fieldKey);
                if (newFields.length === 0) setMSearchQuery('');
                return newFields;
            }
            setMSearchQuery('');
            return [...prev, fieldKey];
        });
    };

    const highlightModalText = (text, fieldKey) => {
        if (!text || text === '-') return text;
        const textStr = String(text);
        let searchTerm = '';
        if (mSelectedSearchFields.length > 0 && fieldKey) searchTerm = (mFieldSearchValues[fieldKey] || '').trim();
        else if (mSelectedSearchFields.length === 0) searchTerm = mSearchQuery.trim();
        if (!searchTerm) return textStr;
        try {
            const escaped = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            const regex = new RegExp(`(${escaped})`, 'gi');
            const parts = textStr.split(regex);
            if (parts.length <= 1) return textStr;
            return parts.map((part, i) => i % 2 === 1 ? <span key={i} style={{ backgroundColor: '#fff176', padding: '0 1px', borderRadius: '2px' }}>{part}</span> : part);
        } catch { return textStr; }
    };

    const toggleExportFileSelection = (fileId) => {
        setSelectedExportIds(prev => {
            const next = new Set(prev);
            if (next.has(fileId)) next.delete(fileId);
            else next.add(fileId);
            return next;
        });
    };

    const allCurrentPageSelected = selectExportFiles.length > 0 && selectExportFiles.every(f => selectedExportIds.has(f.id));

    const toggleSelectAllCurrentPage = () => {
        setSelectedExportIds(prev => {
            const next = new Set(prev);
            if (allCurrentPageSelected) selectExportFiles.forEach(f => next.delete(f.id));
            else selectExportFiles.forEach(f => next.add(f.id));
            return next;
        });
    };

    const exportSelectedFiles = async () => {
        if (selectedExportIds.size === 0) { setError('لم يتم اختيار أي ملف للتصدير'); return; }
        setIsExporting(true);
        setExportSelectModalOpen(false);
        try {
            const res = await axiosInstance.get('/customs-tariffs/');
            const all = Array.isArray(res.data) ? res.data : (res.data.results || []);
            const filtered = all.filter(f => selectedExportIds.has(f.id));
            if (filtered.length === 0) { setError('لم يتم العثور على ملفات للتصدير'); return; }
            buildAndDownloadExcel(filtered);
            setSelectedExportIds(new Set());
        } catch (err) {
            console.error('Error exporting selected to Excel:', err);
            setError('حدث خطأ أثناء التصدير إلى Excel');
        } finally {
            setIsExporting(false);
        }
    };

    // Auto-search debounce inside selection modal
    useEffect(() => {
        if (!exportSelectModalOpen) return;
        if (selectExportDebounceRef.current) clearTimeout(selectExportDebounceRef.current);
        selectExportDebounceRef.current = setTimeout(() => {
            setSelectExportPage(1);
            fetchModalFiles(1, { searchQuery: mSearchQuery, selectedSearchFields: mSelectedSearchFields, fieldSearchValues: mFieldSearchValues });
        }, 400);
        return () => { if (selectExportDebounceRef.current) clearTimeout(selectExportDebounceRef.current); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mSearchQuery, mFieldSearchValues, exportSelectModalOpen]);

    useEffect(() => {
        if (mPrevLoadingRef.current && !isLoadingSelectExport && mActiveInputRef.current) {
            requestAnimationFrame(() => {
                if (mActiveInputRef.current === '__general__' && mGeneralSearchRef.current) mGeneralSearchRef.current.focus();
                else if (mFieldInputRefs.current[mActiveInputRef.current]) mFieldInputRefs.current[mActiveInputRef.current].focus();
            });
        }
        mPrevLoadingRef.current = isLoadingSelectExport;
    }, [isLoadingSelectExport]);

    if (isLoading && tariffs.length === 0) {
        return (
            <div className="loading-container">
                <div className="spinner" style={{ width: '60px', height: '60px' }}></div>
                <p style={{ marginTop: '1rem', color: '#6c757d', fontSize: '1.1rem' }}>جاري التحميل...</p>
            </div>
        );
    }

    return (
        <div className="customs-tariffs-page">
            {/* Header Actions */}
            <div className="page-header">
                <div className="header-left" style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                    <button 
                        className="btn btn-accent btn-large"
                        onClick={() => setIsAddModalOpen(true)}
                    >
                        <svg style={{ width: '24px', height: '24px' }} fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                        </svg>
                        <span>إضافة تعريفة جمركية</span>
                    </button>
                    <button 
                        className="btn btn-large"
                        onClick={() => setExportChoiceModalOpen(true)}
                        disabled={isExporting}
                        style={{
                            background: 'linear-gradient(135deg, #217346 0%, #2e8b57 100%)',
                            color: 'white',
                            border: 'none',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            padding: '0.6rem 1.25rem',
                            borderRadius: '8px',
                            fontWeight: '600',
                            cursor: isExporting ? 'not-allowed' : 'pointer',
                            opacity: isExporting ? 0.7 : 1,
                            transition: 'all 0.2s ease'
                        }}
                    >
                        {isExporting ? (
                            <>
                                <div className="spinner" style={{ width: '18px', height: '18px' }}></div>
                                <span>جاري التصدير...</span>
                            </>
                        ) : (
                            <>
                                <svg style={{ width: '18px', height: '18px' }} fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                                </svg>
                                <span>تصدير Excel</span>
                            </>
                        )}
                    </button>
                </div>

                <div className="view-toggle">
                    <button 
                        className={`toggle-btn ${viewMode === 'table' ? 'active' : ''}`}
                        onClick={() => setViewMode('table')}
                    >
                        <svg style={{ width: '20px', height: '20px' }} fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zm0 6a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1v-2zm0 6a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1v-2z" clipRule="evenodd" />
                        </svg>
                        <span>جدول</span>
                    </button>
                    <button 
                        className={`toggle-btn ${viewMode === 'card' ? 'active' : ''}`}
                        onClick={() => setViewMode('card')}
                    >
                        <svg style={{ width: '20px', height: '20px' }} fill="currentColor" viewBox="0 0 20 20">
                            <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" />
                        </svg>
                        <span>كروت</span>
                    </button>
                </div>
            </div>

            {/* Search Section */}
            <div className="search-section" style={{
                backgroundColor: '#fff',
                borderRadius: '12px',
                padding: '1.5rem',
                marginBottom: '1.5rem',
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                border: '1px solid #e8edf2'
            }}>
                <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start', flexWrap: 'wrap' }}>
                    {/* Search Input */}
                    <div style={{ flex: '1', minWidth: '250px', position: 'relative' }}>
                        <input
                            ref={generalSearchRef}
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && handleSearch(e)}
                            placeholder="ادخل نص البحث..."
                            disabled={selectedSearchFields.length > 0}
                            style={{
                                width: '100%',
                                padding: '0.75rem 1rem',
                                border: '1.5px solid #d5dce4',
                                borderRadius: '8px',
                                fontSize: '1rem',
                                transition: 'all 0.2s ease',
                                outline: 'none',
                                color: selectedSearchFields.length > 0 ? '#a0aec0' : '#4a5568',
                                backgroundColor: selectedSearchFields.length > 0 ? '#f0f2f5' : '#fff',
                                cursor: selectedSearchFields.length > 0 ? 'not-allowed' : 'text'
                            }}
                            onFocus={(e) => { activeInputRef.current = '__general__'; if (selectedSearchFields.length === 0) e.target.style.borderColor = '#5b8abf'; }}
                            onBlur={(e) => e.target.style.borderColor = '#d5dce4'}
                        />
                    </div>
                    
                    {/* Search By Button */}
                    <button
                        onClick={() => setShowSearchOptions(!showSearchOptions)}
                        style={{
                            padding: '0.75rem 1.5rem',
                            backgroundColor: showSearchOptions ? '#03428d' : '#f0f4f8',
                            color: showSearchOptions ? '#fff' : '#5a6f85',
                            border: '1.5px solid ' + (showSearchOptions ? '#03428d' : '#c8d4e0'),
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontWeight: '600',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            transition: 'all 0.2s ease'
                        }}
                    >
                        <svg style={{ width: '18px', height: '18px' }} fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M3 3a1 1 0 011-1h12a1 1 0 011 1v3a1 1 0 01-.293.707L12 11.414V15a1 1 0 01-.293.707l-2 2A1 1 0 018 17v-5.586L3.293 6.707A1 1 0 013 6V3z" clipRule="evenodd" />
                        </svg>
                        البحث بواسطة
                        {selectedSearchFields.length > 0 && (
                            <span style={{
                                backgroundColor: showSearchOptions ? '#fff' : '#03428d',
                                color: showSearchOptions ? '#03428d' : '#fff',
                                borderRadius: '50%',
                                width: '22px',
                                height: '22px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '0.8rem'
                            }}>
                                {selectedSearchFields.length}
                            </span>
                        )}
                    </button>

                    {/* Search Button */}
                    <button
                        type="button"
                        onClick={(e) => handleSearch(e)}
                        style={{
                            padding: '0.75rem 1.5rem',
                            backgroundColor: '#03428d',
                            color: '#fff',
                            border: '1.5px solid #03428d',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontWeight: '600',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            transition: 'all 0.2s ease',
                            minWidth: '120px',
                            justifyContent: 'center'
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#023670';
                            e.currentTarget.style.borderColor = '#023670';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '#03428d';
                            e.currentTarget.style.borderColor = '#03428d';
                        }}
                    >
                        <svg style={{ width: '18px', height: '18px' }} fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                        </svg>
                        بحث
                    </button>

                    {/* Clear Button */}
                    <button
                        type="button"
                        onClick={handleClearSearch}
                        style={{
                            padding: '0.75rem 1.5rem',
                            backgroundColor: '#f7f9fb',
                            color: '#7a8a9e',
                            border: '1.5px solid #d5dce4',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontWeight: '600',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            transition: 'all 0.2s ease',
                            minWidth: '120px',
                            justifyContent: 'center'
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#edf1f5';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '#f7f9fb';
                        }}
                    >
                        <svg style={{ width: '18px', height: '18px' }} fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                        مسح
                    </button>
                </div>

                {/* Search Options Panel */}
                {showSearchOptions && (
                    <div style={{
                        marginTop: '1rem',
                        padding: '1.5rem',
                        backgroundColor: '#f7f9fb',
                        borderRadius: '8px',
                        border: '1px solid #e0e7ef'
                    }}>
                        {/* Text Search Fields with inputs */}
                        <div>
                            <h4 style={{ marginBottom: '1rem', color: '#4a5568', fontSize: '1rem' }}>
                                حقول البحث:
                                <span style={{ fontSize: '0.85rem', fontWeight: '400', color: '#7a8a9e', marginRight: '0.5rem' }}>
                                    (اختر الحقول المطلوبة واكتب قيمة البحث لكل حقل)
                                </span>
                            </h4>
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                                gap: '0.75rem'
                            }}>
                                {searchFields.map(field => {
                                    const isChecked = selectedSearchFields.includes(field.key);
                                    return (
                                        <div
                                            key={field.key}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '0.5rem',
                                                padding: '0.5rem 0.75rem',
                                                backgroundColor: isChecked ? '#eaf2fa' : '#fff',
                                                borderRadius: '8px',
                                                border: isChecked ? '1.5px solid #03428d' : '1px solid #d5dce4',
                                                transition: 'all 0.2s ease'
                                            }}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={isChecked}
                                                onChange={() => toggleSearchField(field.key)}
                                                style={{ 
                                                    width: '16px', 
                                                    height: '16px',
                                                    accentColor: '#03428d',
                                                    cursor: 'pointer',
                                                    flexShrink: 0
                                                }}
                                            />
                                            <span style={{ 
                                                fontSize: '0.88rem', 
                                                color: isChecked ? '#03428d' : '#4a5568',
                                                fontWeight: isChecked ? '600' : '400',
                                                whiteSpace: 'nowrap',
                                                minWidth: '100px',
                                                cursor: 'pointer'
                                            }}
                                                onClick={() => toggleSearchField(field.key)}
                                            >
                                                {field.label}
                                            </span>
                                            <input
                                                ref={el => { fieldInputRefs.current[field.key] = el; }}
                                                type="text"
                                                value={fieldSearchValues[field.key] || ''}
                                                onChange={(e) => {
                                                    if (!isChecked) {
                                                        setSelectedSearchFields(prev => [...prev, field.key]);
                                                    }
                                                    setFieldSearchValues(prev => ({
                                                        ...prev,
                                                        [field.key]: e.target.value
                                                    }));
                                                }}
                                                onKeyPress={(e) => e.key === 'Enter' && handleSearch(e)}
                                                placeholder={isChecked ? `ابحث في ${field.label}...` : ''}
                                                disabled={!isChecked}
                                                style={{
                                                    flex: 1,
                                                    padding: '0.4rem 0.6rem',
                                                    border: isChecked ? '1.5px solid #b0c7e0' : '1px solid #e0e7ef',
                                                    borderRadius: '6px',
                                                    fontSize: '0.88rem',
                                                    outline: 'none',
                                                    backgroundColor: isChecked ? '#fff' : '#f5f5f5',
                                                    color: isChecked ? '#2d3748' : '#a0aec0',
                                                    transition: 'all 0.2s ease',
                                                    cursor: isChecked ? 'text' : 'not-allowed',
                                                    minWidth: '0'
                                                }}
                                                onFocus={(e) => { activeInputRef.current = field.key; if (isChecked) e.target.style.borderColor = '#03428d'; }}
                                                onBlur={(e) => { if (isChecked) e.target.style.borderColor = '#b0c7e0'; }}
                                            />
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                    </div>
                )}
            </div>

            {error && (
                <div className="error-message" style={{ marginBottom: '2rem' }}>
                    <svg style={{ width: '24px', height: '24px' }} fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    <span>{error}</span>
                </div>
            )}

            {isLoading && (
                <div style={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    padding: '2rem',
                    backgroundColor: 'rgba(255,255,255,0.8)',
                    borderRadius: '8px'
                }}>
                    <div className="spinner" style={{ width: '40px', height: '40px' }}></div>
                </div>
            )}

            {!isLoading && tariffs.length === 0 ? (
                <div className="empty-state">
                    <svg style={{ width: '120px', height: '120px', opacity: 0.3 }} fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                    </svg>
                    <h3>لا توجد تعريفات جمركية</h3>
                    <p>قم بإضافة تعريفة جمركية جديدة للبدء</p>
                </div>
            ) : (
                <>
                    {viewMode === 'table' ? (
                        <div className="table-container">
                            <table className="data-table" style={{ minWidth: '1400px', width: '100%' }}>
                                <thead>
                                    <tr>
                                        <th style={{ width: '60px', textAlign: 'center', whiteSpace: 'nowrap' }}>الرقم</th>
                                        <th style={{ width: '180px', minWidth: '180px', maxWidth: '180px', whiteSpace: 'nowrap', textAlign: 'center', position: 'sticky', right: 0, background: '#03428d', zIndex: 12 }}>رقم ملف التخليص</th>
                                        <th style={{ minWidth: '180px', whiteSpace: 'nowrap', textAlign: 'center', position: 'sticky', right: '180px', background: '#03428d', zIndex: 12, boxShadow: '-6px 0 10px rgba(0,0,0,0.2)' }}>التعريفة الجمركية</th>
                                        <th style={{ minWidth: '150px', whiteSpace: 'nowrap', textAlign: 'center' }}>بلد المنشأ</th>
                                        <th style={{ minWidth: '250px', whiteSpace: 'nowrap', textAlign: 'center' }}>وصف الأصناف (عربي)</th>
                                        <th style={{ minWidth: '250px', whiteSpace: 'nowrap', textAlign: 'center' }}>وصف الأصناف (إنجليزي)</th>
                                        <th style={{ minWidth: '250px', whiteSpace: 'nowrap', textAlign: 'center' }}>ملاحظات</th>
                                        <th style={{ minWidth: '180px', whiteSpace: 'nowrap', textAlign: 'center' }}>تاريخ الإنشاء</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {tariffs.map((tariff, index) => {
                                        const selectedItem = getSelectedTariffItem(tariff);
                                        return (
                                            <tr key={tariff.id}>
                                                <td style={{ textAlign: 'center', fontWeight: 600, color: '#6c757d', whiteSpace: 'nowrap' }}>{(currentPage - 1) * 12 + index + 1}</td>
                                                <td style={{ width: '180px', minWidth: '180px', maxWidth: '180px', whiteSpace: 'nowrap', textAlign: 'center', position: 'sticky', right: 0, zIndex: 7 }}>
                                                    <button
                                                        onClick={() => handleEditTariff(tariff)}
                                                        style={{
                                                            background: 'transparent',
                                                            border: 'none',
                                                            padding: '0.5rem 1rem',
                                                            cursor: 'pointer',
                                                            fontWeight: 700,
                                                            color: tariff.clearance_file_num ? '#03428d' : '#6c757d',
                                                            fontSize: 'inherit',
                                                            textDecoration: 'underline',
                                                            transition: 'all 0.2s ease'
                                                        }}
                                                        onMouseEnter={(e) => {
                                                            e.target.style.color = '#03a9b3';
                                                            e.target.style.transform = 'scale(1.05)';
                                                        }}
                                                        onMouseLeave={(e) => {
                                                            e.target.style.color = tariff.clearance_file_num ? '#03428d' : '#6c757d';
                                                            e.target.style.transform = 'scale(1)';
                                                        }}
                                                        title="انقر لتعديل التعريفة الجمركية"
                                                    >
                                                        {highlightText(tariff.clearance_file_num, 'clearance_file_num') || 'غير محدد'}
                                                    </button>
                                                </td>
                                                <td style={{ textAlign: 'center', position: 'sticky', right: '180px', zIndex: 6, boxShadow: '-6px 0 10px rgba(0,0,0,0.2)' }}>
                                                    {tariff.tariffs_list && tariff.tariffs_list.length > 0 ? (
                                                        <select
                                                            value={selectedTariffItems[tariff.id] || tariff.tariffs_list[0].id}
                                                            onChange={(e) => handleTariffItemChange(tariff.id, e.target.value)}
                                                            style={{
                                                                padding: '0.5rem',
                                                                borderRadius: '4px',
                                                                border: '1px solid #ddd',
                                                                backgroundColor: '#fff',
                                                                cursor: 'pointer',
                                                                fontSize: '0.9rem',
                                                                width: '100%',
                                                                minWidth: '150px',
                                                                textAlign: 'center'
                                                            }}
                                                        >
                                                            {tariff.tariffs_list.map(item => (
                                                                <option key={item.id} value={item.id}>
                                                                    {item.custom_tariff_name || '-'}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    ) : (
                                                        <span style={{ color: '#adb5bd' }}>-</span>
                                                    )}
                                                </td>
                                                <td style={{ whiteSpace: 'nowrap', textAlign: 'center' }}>{highlightText(tariff.origin_country_name, 'origin_country') || '-'}</td>
                                                <td style={{ textAlign: 'center' }}>
                                                    <div style={{ maxWidth: '250px', whiteSpace: 'normal', wordWrap: 'break-word', margin: '0 auto' }}>
                                                        {selectedItem ? highlightText(selectedItem.desc_item_ar, 'desc_item_ar') || '-' : '-'}
                                                    </div>
                                                </td>
                                                <td style={{ textAlign: 'center' }}>
                                                    <div style={{ maxWidth: '250px', whiteSpace: 'normal', wordWrap: 'break-word', margin: '0 auto', direction: 'ltr' }}>
                                                        {selectedItem ? highlightText(selectedItem.desc_item_en, 'desc_item_en') || '-' : '-'}
                                                    </div>
                                                </td>
                                                <td style={{ textAlign: 'center' }}>
                                                    <div style={{ maxWidth: '250px', whiteSpace: 'normal', wordWrap: 'break-word', margin: '0 auto', fontSize: '0.9rem' }}>
                                                        {selectedItem ? (selectedItem.note || '-') : '-'}
                                                    </div>
                                                </td>
                                                <td style={{ whiteSpace: 'nowrap', textAlign: 'center' }}>{formatDateTime(tariff.created_at)}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="cards-grid">
                            {tariffs.map(tariff => (
                                <div key={tariff.id} className="tariff-card">
                                    <div className="tariff-card-header">
                                        <button
                                            onClick={() => handleEditTariff(tariff)}
                                            className="tariff-card-file-num"
                                            style={{
                                                background: 'none',
                                                border: 'none',
                                                padding: 0,
                                                cursor: 'pointer',
                                                color: 'inherit',
                                                fontSize: 'inherit',
                                                fontWeight: 'inherit',
                                                textDecoration: 'underline',
                                                transition: 'all 0.2s ease'
                                            }}
                                            onMouseEnter={(e) => {
                                                e.target.style.opacity = '0.8';
                                                e.target.style.transform = 'scale(1.02)';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.target.style.opacity = '1';
                                                e.target.style.transform = 'scale(1)';
                                            }}
                                            title="انقر لتعديل التعريفة الجمركية"
                                        >
                                            رقم ملف التخليص: {tariff.clearance_file_num || 'غير محدد'}
                                        </button>
                                    </div>
                                    <div className="tariff-card-body">
                                        <div className="tariff-card-row">
                                            <svg className="tariff-card-icon" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                                            </svg>
                                            <span className="tariff-card-label">بلد المنشأ:</span>
                                            <span className="tariff-card-value">{tariff.origin_country_name || '-'}</span>
                                        </div>
                                        <div className="tariff-card-row">
                                            <svg className="tariff-card-icon" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M17.707 9.293a1 1 0 010 1.414l-7 7a1 1 0 01-1.414 0l-7-7A.997.997 0 012 10V5a3 3 0 013-3h5c.256 0 .512.098.707.293l7 7zM5 6a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                                            </svg>
                                            <span className="tariff-card-label">التعريفات:</span>
                                            <span className="tariff-card-value">
                                                {tariff.tariffs_list && tariff.tariffs_list.length > 0 
                                                    ? tariff.tariffs_list.map(item => item.custom_tariff_name).filter(Boolean).join(', ') 
                                                    : '-'}
                                            </span>
                                        </div>
                                        <div className="tariff-card-row">
                                            <svg className="tariff-card-icon" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
                                            </svg>
                                            <span className="tariff-card-label">عدد التعريفات:</span>
                                            <span className="tariff-card-value">{tariff.tariffs_list ? tariff.tariffs_list.length : 0}</span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                    
                    {/* Pagination */}
                    {totalPages > 1 && <Pagination />}
                </>
            )}

            {/* Export Choice Modal */}
            {exportChoiceModalOpen && (
                <div onClick={() => setExportChoiceModalOpen(false)} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0, 0, 0, 0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, fontFamily: 'inherit' }}>
                    <div onClick={(e) => e.stopPropagation()} style={{ backgroundColor: '#fff', borderRadius: '16px', padding: '2.25rem 2rem', maxWidth: '520px', width: '92%', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)', textAlign: 'center', fontFamily: 'inherit' }}>
                        <div style={{ width: '72px', height: '72px', borderRadius: '50%', backgroundColor: '#e6f4ea', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.1rem' }}>
                            <svg style={{ width: '38px', height: '38px', color: '#217346' }} fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                            </svg>
                        </div>
                        <h3 style={{ margin: 0, color: '#1e293b', fontSize: '1.5rem', fontWeight: 700, marginBottom: '0.6rem', fontFamily: 'inherit' }}>تصدير إلى Excel</h3>
                        <p style={{ color: '#64748b', marginBottom: '1.85rem', fontSize: '1.05rem', fontFamily: 'inherit' }}>اختر طريقة التصدير المناسبة</p>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
                            <button onClick={() => { setExportChoiceModalOpen(false); exportToExcel(); }} style={{ padding: '1rem 1.25rem', background: 'linear-gradient(135deg, #217346 0%, #2e8b57 100%)', color: '#fff', border: 'none', borderRadius: '12px', fontWeight: 700, fontSize: '1.1rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.65rem', fontFamily: 'inherit' }}>
                                <svg style={{ width: 22, height: 22 }} fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M3 5c0-1.657 3.134-3 7-3s7 1.343 7 3-3.134 3-7 3-7-1.343-7-3z" />
                                    <path d="M3 7.5v3c0 1.657 3.134 3 7 3s7-1.343 7-3v-3c-1.668 1.066-4.157 1.667-7 1.667S4.668 8.566 3 7.5z" />
                                    <path d="M3 12.5v3c0 1.657 3.134 3 7 3s7-1.343 7-3v-3c-1.668 1.066-4.157 1.667-7 1.667S4.668 13.566 3 12.5z" />
                                </svg>
                                تصدير كامل البيانات
                            </button>
                            <button onClick={openExportSelectModal} style={{ padding: '1rem 1.25rem', backgroundColor: '#03428d', color: '#fff', border: 'none', borderRadius: '12px', fontWeight: 700, fontSize: '1.1rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.65rem', fontFamily: 'inherit' }}>
                                <svg style={{ width: 22, height: 22 }} fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4" />
                                    <rect x="3" y="3" width="18" height="18" rx="3" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                                تصدير بيانات محددة
                            </button>
                            <button onClick={() => setExportChoiceModalOpen(false)} style={{ padding: '0.85rem 1.25rem', background: '#f7f9fb', color: '#7a8a9e', border: '1.5px solid #d5dce4', borderRadius: '12px', fontWeight: 700, fontSize: '1rem', cursor: 'pointer', fontFamily: 'inherit' }}>إلغاء</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Select Files for Export Modal */}
            {exportSelectModalOpen && (
                <div onClick={() => setExportSelectModalOpen(false)} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0, 0, 0, 0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: '1rem', fontFamily: 'inherit' }}>
                    <div onClick={(e) => e.stopPropagation()} style={{ backgroundColor: '#fff', borderRadius: '16px', width: '100%', maxWidth: '1400px', height: '92vh', display: 'flex', flexDirection: 'column', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)', overflow: 'hidden', fontFamily: 'inherit' }}>
                        {/* Header */}
                        <div style={{ padding: '1.25rem 1.75rem', backgroundColor: '#03428d', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontFamily: 'inherit' }}>
                            <h3 style={{ margin: 0, fontSize: '1.35rem', fontWeight: 700, color: '#fff', fontFamily: 'inherit' }}>اختر التعريفات المراد تصديرها</h3>
                            <button onClick={() => setExportSelectModalOpen(false)} style={{ background: 'rgba(255,255,255,0.18)', border: 'none', color: '#fff', cursor: 'pointer', fontSize: '1.6rem', lineHeight: 1, width: '36px', height: '36px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
                        </div>

                        {/* Selection summary */}
                        <div style={{ padding: '0.75rem 1.75rem', borderBottom: '1px solid #e8edf2', backgroundColor: '#f9fafc', display: 'flex', gap: '0.85rem', alignItems: 'center', flexWrap: 'wrap', fontFamily: 'inherit' }}>
                            <div style={{ padding: '0.5rem 1rem', backgroundColor: selectedExportIds.size > 0 ? '#e8f4fd' : '#f0f2f5', color: selectedExportIds.size > 0 ? '#03428d' : '#7a8a9e', borderRadius: '10px', fontWeight: 700, fontSize: '0.98rem', border: selectedExportIds.size > 0 ? '1px solid #bee5eb' : '1px solid #e0e7ef', fontFamily: 'inherit' }}>تم اختيار: {selectedExportIds.size}</div>
                            {selectedExportIds.size > 0 && <button onClick={() => setSelectedExportIds(new Set())} style={{ padding: '0.5rem 1rem', backgroundColor: '#fff', color: '#dc3545', border: '1.5px solid #f5c2c7', borderRadius: '10px', fontWeight: 700, fontSize: '0.92rem', cursor: 'pointer', fontFamily: 'inherit' }}>مسح كل الاختيارات</button>}
                        </div>

                        {/* Body */}
                        <div style={{ flex: 1, overflow: 'auto', padding: '1rem 1.5rem', backgroundColor: '#f5f7fa', fontFamily: 'inherit' }}>
                            {/* Search Section */}
                            <div style={{ backgroundColor: '#fff', borderRadius: '12px', padding: '1.25rem', marginBottom: '1.25rem', boxShadow: '0 2px 8px rgba(0,0,0,0.05)', border: '1px solid #e8edf2', fontFamily: 'inherit' }}>
                                <div style={{ display: 'flex', gap: '0.85rem', alignItems: 'flex-start', flexWrap: 'wrap' }}>
                                    <div style={{ flex: '1', minWidth: '230px' }}>
                                        <input ref={mGeneralSearchRef} type="text" value={mSearchQuery} onChange={(e) => setMSearchQuery(e.target.value)} onKeyPress={(e) => e.key === 'Enter' && handleModalSearch(e)} placeholder="ادخل نص البحث..." disabled={mSelectedSearchFields.length > 0} style={{ width: '100%', padding: '0.7rem 1rem', border: '1.5px solid #d5dce4', borderRadius: '8px', fontSize: '0.98rem', outline: 'none', color: mSelectedSearchFields.length > 0 ? '#a0aec0' : '#4a5568', backgroundColor: mSelectedSearchFields.length > 0 ? '#f0f2f5' : '#fff', fontFamily: 'inherit' }} onFocus={() => { mActiveInputRef.current = '__general__'; }} />
                                    </div>
                                    <button onClick={() => setMShowSearchOptions(!mShowSearchOptions)} style={{ padding: '0.7rem 1.25rem', backgroundColor: mShowSearchOptions ? '#03428d' : '#f0f4f8', color: mShowSearchOptions ? '#fff' : '#5a6f85', border: '1.5px solid ' + (mShowSearchOptions ? '#03428d' : '#c8d4e0'), borderRadius: '8px', cursor: 'pointer', fontWeight: 600, fontSize: '0.95rem', display: 'flex', alignItems: 'center', gap: '0.5rem', fontFamily: 'inherit' }}>
                                        <svg style={{ width: 16, height: 16 }} fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M3 3a1 1 0 011-1h12a1 1 0 011 1v3a1 1 0 01-.293.707L12 11.414V15a1 1 0 01-.293.707l-2 2A1 1 0 018 17v-5.586L3.293 6.707A1 1 0 013 6V3z" clipRule="evenodd" /></svg>
                                        البحث بواسطة
                                        {mSelectedSearchFields.length > 0 && <span style={{ backgroundColor: mShowSearchOptions ? '#fff' : '#03428d', color: mShowSearchOptions ? '#03428d' : '#fff', borderRadius: '50%', width: '20px', height: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', fontWeight: 700 }}>{mSelectedSearchFields.length}</span>}
                                    </button>
                                    <button type="button" onClick={handleModalSearch} style={{ padding: '0.7rem 1.25rem', backgroundColor: '#03428d', color: '#fff', border: '1.5px solid #03428d', borderRadius: '8px', cursor: 'pointer', fontWeight: 600, fontSize: '0.95rem', display: 'flex', alignItems: 'center', gap: '0.5rem', fontFamily: 'inherit' }}>
                                        <svg style={{ width: 16, height: 16 }} fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" /></svg>
                                        بحث
                                    </button>
                                    <button type="button" onClick={handleModalClearSearch} style={{ padding: '0.7rem 1.25rem', backgroundColor: '#f7f9fb', color: '#7a8a9e', border: '1.5px solid #d5dce4', borderRadius: '8px', cursor: 'pointer', fontWeight: 600, fontSize: '0.95rem', display: 'flex', alignItems: 'center', gap: '0.5rem', fontFamily: 'inherit' }}>
                                        <svg style={{ width: 16, height: 16 }} fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" /></svg>
                                        مسح
                                    </button>
                                </div>
                                {mShowSearchOptions && (
                                    <div style={{ marginTop: '1rem', padding: '1.25rem', backgroundColor: '#f7f9fb', borderRadius: '8px', border: '1px solid #e0e7ef', fontFamily: 'inherit' }}>
                                        <h4 style={{ marginBottom: '0.85rem', color: '#4a5568', fontSize: '0.98rem', fontFamily: 'inherit' }}>حقول البحث:</h4>
                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '0.7rem' }}>
                                            {searchFields.map(field => {
                                                const isChecked = mSelectedSearchFields.includes(field.key);
                                                return (
                                                    <div key={field.key} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.45rem 0.7rem', backgroundColor: isChecked ? '#eaf2fa' : '#fff', borderRadius: '8px', border: isChecked ? '1.5px solid #03428d' : '1px solid #d5dce4' }}>
                                                        <input type="checkbox" checked={isChecked} onChange={() => toggleModalSearchField(field.key)} style={{ width: 16, height: 16, accentColor: '#03428d', cursor: 'pointer' }} />
                                                        <span style={{ fontSize: '0.86rem', color: isChecked ? '#03428d' : '#4a5568', fontWeight: isChecked ? 600 : 400, minWidth: '90px', cursor: 'pointer', fontFamily: 'inherit' }} onClick={() => toggleModalSearchField(field.key)}>{field.label}</span>
                                                        <input ref={el => { mFieldInputRefs.current[field.key] = el; }} type="text" value={mFieldSearchValues[field.key] || ''} onChange={(e) => { if (!isChecked) setMSelectedSearchFields(prev => [...prev, field.key]); setMFieldSearchValues(prev => ({ ...prev, [field.key]: e.target.value })); }} onKeyPress={(e) => e.key === 'Enter' && handleModalSearch(e)} placeholder={isChecked ? `ابحث...` : ''} disabled={!isChecked} style={{ flex: 1, padding: '0.35rem 0.55rem', border: isChecked ? '1.5px solid #b0c7e0' : '1px solid #e0e7ef', borderRadius: '6px', fontSize: '0.85rem', outline: 'none', backgroundColor: isChecked ? '#fff' : '#f5f5f5', color: isChecked ? '#2d3748' : '#a0aec0', cursor: isChecked ? 'text' : 'not-allowed', fontFamily: 'inherit' }} onFocus={() => { mActiveInputRef.current = field.key; }} />
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Table */}
                            {isLoadingSelectExport && selectExportFiles.length === 0 ? (
                                <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}><div className="spinner" style={{ width: 36, height: 36 }}></div></div>
                            ) : selectExportFiles.length === 0 ? (
                                <div style={{ textAlign: 'center', padding: '3rem 1rem', color: '#7a8a9e', fontFamily: 'inherit', backgroundColor: '#fff', borderRadius: '12px', border: '1px solid #e8edf2' }}>
                                    <svg style={{ width: 60, height: 60, opacity: 0.3, marginBottom: '0.75rem' }} fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" /></svg>
                                    <div style={{ fontSize: '1.1rem', fontWeight: 700 }}>لا توجد تعريفات مطابقة</div>
                                </div>
                            ) : (
                                <>
                                    {isLoadingSelectExport && <div style={{ display: 'flex', justifyContent: 'center', padding: '0.5rem 0', backgroundColor: 'rgba(255,255,255,0.6)', borderRadius: '8px', marginBottom: '0.5rem' }}><div className="spinner" style={{ width: 24, height: 24 }}></div></div>}
                                    <div style={{ overflowX: 'auto', backgroundColor: '#fff', borderRadius: '9px', boxShadow: '0 2px 9px rgba(0,0,0,0.08)' }}>
                                        <table style={{ minWidth: '1000px', width: '100%', borderCollapse: 'collapse', fontFamily: 'inherit' }}>
                                            <thead>
                                                <tr>
                                                    <th style={{ width: 60, textAlign: 'center', padding: '0.85rem 0.5rem', background: '#03428d', color: '#fff', fontWeight: 700, fontSize: '0.95rem', position: 'sticky', top: 0, zIndex: 10 }}>
                                                        <input type="checkbox" checked={allCurrentPageSelected} onChange={toggleSelectAllCurrentPage} style={{ width: 18, height: 18, accentColor: '#fff', cursor: 'pointer' }} />
                                                    </th>
                                                    {['الرقم', 'رقم ملف التخليص', 'التعريفات الجمركية', 'بلد المنشأ', 'تاريخ الإنشاء'].map((h, i) => (
                                                        <th key={i} style={{ padding: '0.85rem 0.6rem', background: '#03428d', color: '#fff', fontWeight: 700, fontSize: '0.95rem', textAlign: 'center', whiteSpace: 'nowrap', position: 'sticky', top: 0, zIndex: 10 }}>{h}</th>
                                                    ))}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {selectExportFiles.map((tariff, index) => {
                                                    const isChecked = selectedExportIds.has(tariff.id);
                                                    const rowBg = isChecked ? '#eaf2fa' : (index % 2 === 0 ? '#fff' : '#fafbfc');
                                                    const tdStyle = { padding: '0.7rem 0.6rem', borderBottom: '1px solid #e8edf2', color: '#2d3748', fontSize: '0.92rem', textAlign: 'center', fontFamily: 'inherit' };
                                                    return (
                                                        <tr key={tariff.id} onClick={() => toggleExportFileSelection(tariff.id)} style={{ backgroundColor: rowBg, cursor: 'pointer' }}>
                                                            <td style={{ ...tdStyle, backgroundColor: rowBg }}><input type="checkbox" checked={isChecked} onChange={() => toggleExportFileSelection(tariff.id)} onClick={(e) => e.stopPropagation()} style={{ width: 18, height: 18, accentColor: '#03428d', cursor: 'pointer' }} /></td>
                                                            <td style={{ ...tdStyle, fontWeight: 600, color: '#6c757d' }}>{(selectExportPage - 1) * 12 + index + 1}</td>
                                                            <td style={{ ...tdStyle, fontWeight: 700, color: '#03428d' }}>{highlightModalText(tariff.clearance_file_num, 'clearance_file_num') || '-'}</td>
                                                            <td style={tdStyle}>{tariff.tariffs_list && tariff.tariffs_list.length > 0 ? tariff.tariffs_list.map((item, idx) => <span key={idx}>{highlightModalText(item.custom_tariff_name, 'custom_tariff') || '-'}{idx < tariff.tariffs_list.length - 1 && ', '}</span>) : '-'}</td>
                                                            <td style={tdStyle}>{highlightModalText(tariff.origin_country_name, 'origin_country') || '-'}</td>
                                                            <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>{formatDateTime(tariff.created_at)}</td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                    {selectExportTotalPages > 1 && (
                                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.5rem', marginTop: '1.25rem', padding: '0.75rem', flexWrap: 'wrap', fontFamily: 'inherit' }}>
                                            <button onClick={() => handleModalPageChange(1)} disabled={selectExportPage === 1} style={{ padding: '0.5rem 1rem', border: '1px solid #ddd', borderRadius: '8px', backgroundColor: selectExportPage === 1 ? '#f5f5f5' : '#fff', color: selectExportPage === 1 ? '#aaa' : '#03428d', cursor: selectExportPage === 1 ? 'not-allowed' : 'pointer', fontWeight: 600, fontFamily: 'inherit' }}>الأولى</button>
                                            <button onClick={() => handleModalPageChange(selectExportPage - 1)} disabled={selectExportPage === 1} style={{ padding: '0.5rem 1rem', border: '1px solid #ddd', borderRadius: '8px', backgroundColor: selectExportPage === 1 ? '#f5f5f5' : '#fff', color: selectExportPage === 1 ? '#aaa' : '#03428d', cursor: selectExportPage === 1 ? 'not-allowed' : 'pointer', fontWeight: 600, fontFamily: 'inherit' }}>السابق</button>
                                            <div style={{ padding: '0.5rem 1rem', backgroundColor: '#03428d', color: '#fff', borderRadius: '8px', fontWeight: 600, fontFamily: 'inherit' }}>صفحة {selectExportPage} من {selectExportTotalPages} <span style={{ opacity: 0.8, fontSize: '0.85rem' }}>({selectExportTotalCount} نتيجة)</span></div>
                                            <button onClick={() => handleModalPageChange(selectExportPage + 1)} disabled={selectExportPage === selectExportTotalPages} style={{ padding: '0.5rem 1rem', border: '1px solid #ddd', borderRadius: '8px', backgroundColor: selectExportPage === selectExportTotalPages ? '#f5f5f5' : '#fff', color: selectExportPage === selectExportTotalPages ? '#aaa' : '#03428d', cursor: selectExportPage === selectExportTotalPages ? 'not-allowed' : 'pointer', fontWeight: 600, fontFamily: 'inherit' }}>التالي</button>
                                            <button onClick={() => handleModalPageChange(selectExportTotalPages)} disabled={selectExportPage === selectExportTotalPages} style={{ padding: '0.5rem 1rem', border: '1px solid #ddd', borderRadius: '8px', backgroundColor: selectExportPage === selectExportTotalPages ? '#f5f5f5' : '#fff', color: selectExportPage === selectExportTotalPages ? '#aaa' : '#03428d', cursor: selectExportPage === selectExportTotalPages ? 'not-allowed' : 'pointer', fontWeight: 600, fontFamily: 'inherit' }}>الأخيرة</button>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>

                        {/* Footer */}
                        <div style={{ padding: '1rem 1.75rem', borderTop: '1px solid #e8edf2', backgroundColor: '#fff', display: 'flex', justifyContent: 'flex-end', gap: '0.85rem', fontFamily: 'inherit' }}>
                            <button onClick={() => setExportSelectModalOpen(false)} style={{ padding: '0.75rem 1.5rem', backgroundColor: '#f7f9fb', color: '#7a8a9e', border: '1.5px solid #d5dce4', borderRadius: 10, fontWeight: 700, fontSize: '1rem', cursor: 'pointer', fontFamily: 'inherit' }}>إلغاء</button>
                            <button onClick={exportSelectedFiles} disabled={selectedExportIds.size === 0 || isExporting} style={{ padding: '0.75rem 1.5rem', background: (selectedExportIds.size === 0 || isExporting) ? '#cbd5e0' : 'linear-gradient(135deg, #217346 0%, #2e8b57 100%)', color: '#fff', border: 'none', borderRadius: 10, fontWeight: 700, fontSize: '1rem', cursor: (selectedExportIds.size === 0 || isExporting) ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: '0.55rem', fontFamily: 'inherit' }}>
                                <svg style={{ width: 20, height: 20 }} fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" /></svg>
                                تصدير المحدد ({selectedExportIds.size})
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Add Tariff Modal */}
            <AddCustomsTariffsModal
                isOpen={isAddModalOpen}
                onClose={() => setIsAddModalOpen(false)}
                onSuccess={handleAddSuccess}
            />

            {/* Edit Tariff Modal */}
            <EditCustomsTariffsModal
                isOpen={isEditModalOpen}
                onClose={() => {
                    setIsEditModalOpen(false);
                    setSelectedTariffForEdit(null);
                }}
                onSuccess={handleEditSuccess}
                tariffData={selectedTariffForEdit}
            />
        </div>
    );
};

export default CustomsTariffs;
